/* ------------------------------------------------------------------ */
/*  Wazuh Rule Builder – UI script                                    */
/* ------------------------------------------------------------------ */

import { runPipeline } from '../../lib/wazuh-rules/engine';
import { scenarios, presetLogs } from '../../lib/wazuh-rules/scenarios';
import { classifyLines, calculateScore } from '../../lib/wazuh-rules/scoring';
import type {
  Scenario,
  FullTestResult,
  LineClassification,
  ChallengeScore,
} from '../../lib/wazuh-rules/types';

/* ------------------------------------------------------------------ */
/*  DOM elements                                                      */
/* ------------------------------------------------------------------ */

const $ = <T extends HTMLElement>(id: string): T => {
  const el = document.getElementById(id) as T | null;
  if (!el) throw new Error(`Element #${id} not found`);
  return el;
};

// Mode toggles
const modeSandbox = $<HTMLButtonElement>('wrb-mode-sandbox');
const modeChallenge = $<HTMLButtonElement>('wrb-mode-challenge');
const sandboxPane = $<HTMLDivElement>('wrb-sandbox');
const challengePane = $<HTMLDivElement>('wrb-challenge');

// Sandbox elements
const logsInput = $<HTMLTextAreaElement>('wrb-logs');
const presetSelect = $<HTMLSelectElement>('wrb-preset-select');
const decoderInput = $<HTMLTextAreaElement>('wrb-decoder');
const ruleInput = $<HTMLTextAreaElement>('wrb-rule');
const testBtn = $<HTMLButtonElement>('wrb-test-btn');
const statusEl = $<HTMLSpanElement>('wrb-status');
const messagesCard = $<HTMLDivElement>('wrb-messages');
const messagesList = $<HTMLDivElement>('wrb-messages-list');
const dismissMessages = $<HTMLButtonElement>('wrb-dismiss-messages');
const resultsCard = $<HTMLDivElement>('wrb-results');
const resultSummary = $<HTMLSpanElement>('wrb-result-summary');
const resultsList = $<HTMLDivElement>('wrb-results-list');

// Challenge elements
const scenarioPicker = $<HTMLDivElement>('wrb-scenario-picker');
const activeChallenge = $<HTMLDivElement>('wrb-active-challenge');
const challengeTitle = $<HTMLHeadingElement>('wrb-challenge-title');
const challengeDifficulty = $<HTMLSpanElement>('wrb-challenge-difficulty');
const challengeObjective = $<HTMLParagraphElement>('wrb-challenge-objective');
const challengeLogs = $<HTMLTextAreaElement>('wrb-challenge-logs');
const logCount = $<HTMLSpanElement>('wrb-log-count');
const challengeDecoder = $<HTMLTextAreaElement>('wrb-challenge-decoder');
const challengeRule = $<HTMLTextAreaElement>('wrb-challenge-rule');
const challengeTestBtn = $<HTMLButtonElement>('wrb-challenge-test-btn');
const showSolution = $<HTMLButtonElement>('wrb-show-solution');
const backToScenarios = $<HTMLButtonElement>('wrb-back-to-scenarios');
const challengeMessagesCard = $<HTMLDivElement>('wrb-challenge-messages');
const challengeMessagesList = $<HTMLDivElement>('wrb-challenge-messages-list');
const challengeDismiss = $<HTMLButtonElement>('wrb-challenge-dismiss');
const scoringCard = $<HTMLDivElement>('wrb-scoring');
const gradeBadge = $<HTMLDivElement>('wrb-grade-badge');
const gradeLabel = $<HTMLDivElement>('wrb-grade-label');
const scorePoints = $<HTMLDivElement>('wrb-score-points');
const precisionEl = $<HTMLDivElement>('wrb-precision');
const recallEl = $<HTMLDivElement>('wrb-recall');
const f1El = $<HTMLDivElement>('wrb-f1');
const tpEl = $<HTMLDivElement>('wrb-tp');
const fpEl = $<HTMLDivElement>('wrb-fp');
const fnEl = $<HTMLDivElement>('wrb-fn');
const tnEl = $<HTMLDivElement>('wrb-tn');
const challengeResults = $<HTMLDivElement>('wrb-challenge-results');
const challengeResultsList = $<HTMLDivElement>('wrb-challenge-results-list');

/* ------------------------------------------------------------------ */
/*  State                                                             */
/* ------------------------------------------------------------------ */

let currentMode: 'sandbox' | 'challenge' = 'sandbox';
let currentScenario: Scenario | null = null;

/* ------------------------------------------------------------------ */
/*  Mode switching                                                    */
/* ------------------------------------------------------------------ */

function setMode(mode: 'sandbox' | 'challenge') {
  currentMode = mode;
  sandboxPane.classList.toggle('hidden', mode !== 'sandbox');
  challengePane.classList.toggle('hidden', mode !== 'challenge');

  modeSandbox.classList.toggle('border-accent', mode === 'sandbox');
  modeSandbox.classList.toggle('text-accent', mode === 'sandbox');
  modeSandbox.classList.toggle('border-border/70', mode !== 'sandbox');
  modeSandbox.classList.toggle('text-muted', mode !== 'sandbox');

  modeChallenge.classList.toggle('border-accent', mode === 'challenge');
  modeChallenge.classList.toggle('text-accent', mode === 'challenge');
  modeChallenge.classList.toggle('border-border/70', mode !== 'challenge');
  modeChallenge.classList.toggle('text-muted', mode !== 'challenge');

  if (mode === 'challenge' && !currentScenario) {
    showScenarioPicker();
  }
}

modeSandbox.addEventListener('click', () => setMode('sandbox'));
modeChallenge.addEventListener('click', () => setMode('challenge'));

/* ------------------------------------------------------------------ */
/*  Preset log loader                                                 */
/* ------------------------------------------------------------------ */

for (const preset of presetLogs) {
  const opt = document.createElement('option');
  opt.value = preset.label;
  opt.textContent = preset.label;
  presetSelect.appendChild(opt);
}

presetSelect.addEventListener('change', () => {
  const preset = presetLogs.find((p) => p.label === presetSelect.value);
  if (preset) {
    logsInput.value = preset.logs;
    presetSelect.value = '';
  }
});

/* ------------------------------------------------------------------ */
/*  Tab key handling in textareas                                     */
/* ------------------------------------------------------------------ */

function handleTab(e: KeyboardEvent) {
  if (e.key === 'Tab') {
    e.preventDefault();
    const ta = e.target as HTMLTextAreaElement;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    ta.value = ta.value.substring(0, start) + '  ' + ta.value.substring(end);
    ta.selectionStart = ta.selectionEnd = start + 2;
  }
}

[logsInput, decoderInput, ruleInput, challengeDecoder, challengeRule].forEach((ta) => {
  ta.addEventListener('keydown', handleTab);
});

/* ------------------------------------------------------------------ */
/*  Escape HTML                                                       */
/* ------------------------------------------------------------------ */

function esc(s: string): string {
  const d = document.createElement('div');
  d.textContent = s;
  return d.innerHTML;
}

/* ------------------------------------------------------------------ */
/*  Sandbox: render results                                           */
/* ------------------------------------------------------------------ */

function levelColor(level: number): string {
  if (level >= 12) return 'text-rose-500';
  if (level >= 8) return 'text-orange-500';
  if (level >= 5) return 'text-amber-500';
  if (level >= 3) return 'text-blue-500';
  return 'text-muted';
}

function levelBorder(level: number): string {
  if (level >= 12) return 'border-rose-500/30 bg-rose-500/5';
  if (level >= 8) return 'border-orange-500/30 bg-orange-500/5';
  if (level >= 5) return 'border-amber-500/30 bg-amber-500/5';
  if (level >= 3) return 'border-blue-500/30 bg-blue-500/5';
  return 'border-border/30';
}

function renderSandboxResults(result: FullTestResult) {
  // Messages
  if (result.messages.length > 0) {
    messagesCard.classList.remove('hidden');
    messagesList.innerHTML = result.messages
      .map((m) => {
        const color = m.type === 'error' ? 'text-rose-400' : 'text-amber-400';
        return `<div class="${color}">[${m.type}] ${esc(m.message)}</div>`;
      })
      .join('');
  } else {
    messagesCard.classList.add('hidden');
  }

  // Results
  resultsCard.classList.remove('hidden');

  const totalRulesFired = result.lines.reduce((sum, l) => sum + l.firedRules.length, 0);
  const linesWithRules = result.lines.filter((l) => l.firedRules.length > 0).length;
  resultSummary.textContent =
    `${result.decoderCount} decoder(s), ${result.ruleCount} rule(s) — ` +
    `${totalRulesFired} rule(s) fired across ${linesWithRules}/${result.lines.length} line(s)`;

  resultsList.innerHTML = result.lines
    .map((line) => {
      const maxLevel = line.firedRules.reduce((max, r) => Math.max(max, r.level), 0);
      const borderClass =
        line.firedRules.length > 0 ? levelBorder(maxLevel) : 'border-border/30';

      const decoderBadge = line.decoded.decoder
        ? `<span class="rounded-full border border-accent/30 bg-accent/5 px-2 py-0.5 text-xs text-accent">${esc(line.decoded.decoder)}</span>`
        : `<span class="rounded-full border border-border/30 px-2 py-0.5 text-xs text-muted">no decoder match</span>`;

      const fields = Object.entries(line.decoded.fields);
      const fieldsHtml =
        fields.length > 0
          ? `<div class="mt-1 flex flex-wrap gap-1">${fields
              .map(
                ([k, v]) =>
                  `<span class="rounded bg-surface border border-border/50 px-1.5 py-0.5 text-xs"><span class="text-muted">${esc(k)}=</span>${esc(v)}</span>`
              )
              .join('')}</div>`
          : '';

      const rulesHtml =
        line.firedRules.length > 0
          ? `<div class="mt-2 space-y-1">${line.firedRules
              .map(
                (r) =>
                  `<div class="flex items-center gap-2 text-xs">` +
                  `<span class="rounded-full px-2 py-0.5 font-medium ${levelColor(r.level)} ${levelBorder(r.level)}">Level ${r.level}</span>` +
                  `<span class="text-muted">Rule ${r.id}:</span>` +
                  `<span class="text-text">${esc(r.description)}</span>` +
                  (r.mitre
                    ? `<span class="rounded bg-purple-500/10 border border-purple-500/30 px-1.5 py-0.5 text-purple-400">${esc(r.mitre.id)}</span>`
                    : '') +
                  `</div>`
              )
              .join('')}</div>`
          : '';

      return (
        `<div class="rounded-lg border ${borderClass} p-3">` +
        `<div class="flex items-start gap-2">` +
        `<span class="shrink-0 rounded bg-surface border border-border/50 px-1.5 py-0.5 text-xs text-muted tabular-nums">${line.lineIndex + 1}</span>` +
        `<code class="text-xs text-text break-all">${esc(line.raw)}</code>` +
        `</div>` +
        `<div class="mt-2 flex items-center gap-2">${decoderBadge}</div>` +
        fieldsHtml +
        rulesHtml +
        `</div>`
      );
    })
    .join('');
}

/* ------------------------------------------------------------------ */
/*  Sandbox: run test                                                 */
/* ------------------------------------------------------------------ */

function runSandboxTest() {
  const lines = logsInput.value.split('\n');
  const decoderXml = decoderInput.value;
  const ruleXml = ruleInput.value;

  if (!logsInput.value.trim()) {
    statusEl.textContent = 'Enter log lines first.';
    return;
  }

  const t0 = performance.now();
  const result = runPipeline(lines, decoderXml, ruleXml);
  const elapsed = (performance.now() - t0).toFixed(1);

  statusEl.textContent = `Completed in ${elapsed}ms`;
  renderSandboxResults(result);
}

testBtn.addEventListener('click', runSandboxTest);

dismissMessages.addEventListener('click', () => {
  messagesCard.classList.add('hidden');
});

/* ------------------------------------------------------------------ */
/*  Challenge: scenario picker                                        */
/* ------------------------------------------------------------------ */

const difficultyColors: Record<string, { border: string; bg: string; text: string }> = {
  beginner: {
    border: 'border-emerald-500/30',
    bg: 'bg-emerald-500/5',
    text: 'text-emerald-500',
  },
  intermediate: {
    border: 'border-amber-500/30',
    bg: 'bg-amber-500/5',
    text: 'text-amber-500',
  },
  advanced: { border: 'border-rose-500/30', bg: 'bg-rose-500/5', text: 'text-rose-500' },
};

function showScenarioPicker() {
  currentScenario = null;
  scenarioPicker.classList.remove('hidden');
  activeChallenge.classList.add('hidden');

  scenarioPicker.innerHTML = scenarios
    .map((s) => {
      const dc = difficultyColors[s.difficulty];
      return (
        `<button data-scenario="${s.id}" class="rounded-xl border ${dc.border} ${dc.bg} p-4 text-left transition hover:border-accent/50">` +
        `<div class="flex items-center gap-2 mb-2">` +
        `<span class="rounded-full border ${dc.border} px-2 py-0.5 text-xs font-medium uppercase tracking-wider ${dc.text}">${s.difficulty}</span>` +
        `</div>` +
        `<div class="text-lg font-medium text-text">${esc(s.title)}</div>` +
        `<div class="text-xs text-muted mt-2">${esc(s.description)}</div>` +
        `<div class="mt-3 text-xs text-muted">${s.logs.length} log lines</div>` +
        `</button>`
      );
    })
    .join('');

  scenarioPicker.querySelectorAll<HTMLButtonElement>('[data-scenario]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.dataset.scenario!;
      const scenario = scenarios.find((s) => s.id === id);
      if (scenario) loadScenario(scenario);
    });
  });
}

/* ------------------------------------------------------------------ */
/*  Challenge: load scenario                                          */
/* ------------------------------------------------------------------ */

function loadScenario(scenario: Scenario) {
  currentScenario = scenario;
  scenarioPicker.classList.add('hidden');
  activeChallenge.classList.remove('hidden');

  // Header
  const dc = difficultyColors[scenario.difficulty];
  challengeTitle.textContent = scenario.title;
  challengeDifficulty.textContent = scenario.difficulty;
  challengeDifficulty.className = `rounded-full border px-3 py-1 text-xs font-medium uppercase tracking-wider ${dc.border} ${dc.text}`;
  challengeObjective.textContent = scenario.objective;

  // Logs
  const logText = scenario.logs.map((l) => l.log).join('\n');
  challengeLogs.value = logText;
  logCount.textContent = `${scenario.logs.length} lines`;

  // Hints
  challengeDecoder.value = scenario.decoderHint;
  challengeRule.value = scenario.ruleHint;

  // Reset scoring
  scoringCard.classList.add('hidden');
  challengeResults.classList.add('hidden');
  challengeMessagesCard.classList.add('hidden');
}

/* ------------------------------------------------------------------ */
/*  Challenge: run test                                               */
/* ------------------------------------------------------------------ */

function runChallengeTest() {
  if (!currentScenario) return;

  const lines = currentScenario.logs.map((l) => l.log);
  const decoderXml = challengeDecoder.value;
  const ruleXml = challengeRule.value;

  const result = runPipeline(lines, decoderXml, ruleXml);

  // Messages
  if (result.messages.length > 0) {
    challengeMessagesCard.classList.remove('hidden');
    challengeMessagesList.innerHTML = result.messages
      .map((m) => {
        const color = m.type === 'error' ? 'text-rose-400' : 'text-amber-400';
        return `<div class="${color}">[${m.type}] ${esc(m.message)}</div>`;
      })
      .join('');
  } else {
    challengeMessagesCard.classList.add('hidden');
  }

  // Scoring
  const score = calculateScore(result, currentScenario.logs);
  renderScoring(score);

  // Per-line breakdown
  const classifications = classifyLines(result, currentScenario.logs);
  renderChallengeResults(result, classifications);
}

challengeTestBtn.addEventListener('click', runChallengeTest);

challengeDismiss.addEventListener('click', () => {
  challengeMessagesCard.classList.add('hidden');
});

/* ------------------------------------------------------------------ */
/*  Challenge: render scoring                                         */
/* ------------------------------------------------------------------ */

const gradeColors: Record<string, { border: string; text: string }> = {
  S: { border: 'border-purple-500', text: 'text-purple-500' },
  A: { border: 'border-emerald-500', text: 'text-emerald-500' },
  B: { border: 'border-blue-500', text: 'text-blue-500' },
  C: { border: 'border-amber-500', text: 'text-amber-500' },
  D: { border: 'border-rose-500', text: 'text-rose-500' },
};

function renderScoring(score: ChallengeScore) {
  scoringCard.classList.remove('hidden');

  const gc = gradeColors[score.grade];
  gradeBadge.textContent = score.grade;
  gradeBadge.className = `flex h-16 w-16 items-center justify-center rounded-full border-2 text-2xl font-bold ${gc.border} ${gc.text}`;
  gradeLabel.textContent = score.gradeLabel;
  scorePoints.textContent = `${score.points} / ${score.maxPoints} points`;

  precisionEl.textContent = `${(score.precision * 100).toFixed(0)}%`;
  recallEl.textContent = `${(score.recall * 100).toFixed(0)}%`;
  f1El.textContent = `${(score.f1 * 100).toFixed(0)}%`;

  tpEl.textContent = String(score.matrix.tp);
  fpEl.textContent = String(score.matrix.fp);
  fnEl.textContent = String(score.matrix.fn);
  tnEl.textContent = String(score.matrix.tn);
}

/* ------------------------------------------------------------------ */
/*  Challenge: per-line breakdown                                     */
/* ------------------------------------------------------------------ */

const categoryMeta: Record<string, { label: string; color: string; border: string }> = {
  tp: {
    label: 'TP',
    color: 'text-emerald-500',
    border: 'border-emerald-500/30 bg-emerald-500/5',
  },
  tn: {
    label: 'TN',
    color: 'text-emerald-500',
    border: 'border-emerald-500/30 bg-emerald-500/5',
  },
  fp: { label: 'FP', color: 'text-rose-500', border: 'border-rose-500/30 bg-rose-500/5' },
  fn: { label: 'FN', color: 'text-rose-500', border: 'border-rose-500/30 bg-rose-500/5' },
};

function renderChallengeResults(
  result: FullTestResult,
  classifications: LineClassification[]
) {
  challengeResults.classList.remove('hidden');

  challengeResultsList.innerHTML = classifications
    .map((c) => {
      const cm = categoryMeta[c.category];
      const line = result.lines[c.lineIndex];
      const rulesHtml =
        line.firedRules.length > 0
          ? line.firedRules
              .map(
                (r) =>
                  `<span class="text-xs ${levelColor(r.level)}">Rule ${r.id} (L${r.level}): ${esc(r.description)}</span>`
              )
              .join('<br/>')
          : '<span class="text-xs text-muted">No rules fired</span>';

      return (
        `<div class="rounded-lg border ${cm.border} p-3">` +
        `<div class="flex items-start gap-2">` +
        `<span class="shrink-0 rounded-full px-2 py-0.5 text-xs font-bold ${cm.color} ${cm.border}">${cm.label}</span>` +
        `<div class="min-w-0">` +
        `<code class="text-xs text-text break-all block">${esc(c.raw)}</code>` +
        `<div class="mt-1 text-xs text-muted">${esc(c.reason)}</div>` +
        `<div class="mt-1">${rulesHtml}</div>` +
        `</div>` +
        `</div>` +
        `</div>`
      );
    })
    .join('');
}

/* ------------------------------------------------------------------ */
/*  Challenge: show solution                                          */
/* ------------------------------------------------------------------ */

function showSolutionFn() {
  if (!currentScenario) return;
  challengeDecoder.value = currentScenario.solutionDecoder;
  challengeRule.value = currentScenario.solutionRule;
  runChallengeTest();
}

showSolution.addEventListener('click', showSolutionFn);

backToScenarios.addEventListener('click', () => {
  showScenarioPicker();
});

/* ------------------------------------------------------------------ */
/*  Keyboard shortcuts                                                */
/* ------------------------------------------------------------------ */

document.addEventListener('keydown', (e) => {
  // Ctrl+Enter: run test
  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
    e.preventDefault();
    if (currentMode === 'sandbox') {
      runSandboxTest();
    } else if (currentScenario) {
      runChallengeTest();
    }
    return;
  }

  // Don't handle shortcuts while typing in textareas/inputs
  const tag = (e.target as HTMLElement).tagName;
  if (tag === 'TEXTAREA' || tag === 'INPUT' || tag === 'SELECT') return;

  switch (e.key) {
    case 'Escape':
      messagesCard.classList.add('hidden');
      challengeMessagesCard.classList.add('hidden');
      break;
    case '1':
      if (currentMode === 'sandbox') logsInput.focus();
      else if (currentScenario) challengeLogs.focus();
      break;
    case '2':
      if (currentMode === 'sandbox') decoderInput.focus();
      else if (currentScenario) challengeDecoder.focus();
      break;
    case '3':
      if (currentMode === 'sandbox') ruleInput.focus();
      else if (currentScenario) challengeRule.focus();
      break;
    case 's':
    case 'S':
      if (currentMode === 'challenge' && currentScenario) {
        e.preventDefault();
        showSolutionFn();
      }
      break;
  }
});

/* ------------------------------------------------------------------ */
/*  Initialize                                                        */
/* ------------------------------------------------------------------ */

// Build scenario picker on first load of challenge mode
showScenarioPicker();
